import { BlocksUser } from "@/src/models/user.model";

const DEFAULT_ORG_ID = "default";

/**
 * Returns true when the given preferred org id is still a valid (non-default)
 * membership for this user.
 */
export function isPreferredOrgValid(
  preferredOrgId: string | null | undefined,
  memberships: BlocksUser["memberships"],
): boolean {
  if (!preferredOrgId || preferredOrgId === DEFAULT_ORG_ID) return false;
  return memberships.some((m) => m.organizationId === preferredOrgId);
}

/**
 * Returns true when the auto-switch to the preferred org should happen:
 * the preferred org is valid (still in memberships) AND the current JWT org_id
 * is not already that org.
 */
export function shouldAutoSwitchToPreferredOrg(
  preferredOrgId: string | null | undefined,
  currentOrgId: string | null | undefined,
  memberships: BlocksUser["memberships"],
): boolean {
  if (!isPreferredOrgValid(preferredOrgId, memberships)) return false;
  return preferredOrgId !== currentOrgId;
}

/**
 * Routes that carry their own one-time credential in the URL and therefore own
 * where the user lands — the post-login redirect must leave them alone.
 *
 * Both are reached by clicking a link in an email, and both break if the session
 * happens to still be valid at that moment:
 *   - `reset-password` — "Change Password" in Settings calls `/auth/recover`
 *     WITHOUT logging the user out, so the mail is nearly always opened by a
 *     still-authenticated user. This one broke every time.
 *   - `set-password` — the admin-invite activation mail. Normally opened by a
 *     brand-new user with no session, so the redirect never fired and the bug
 *     stayed latent; it surfaces only when someone already signed in opens it.
 *
 * Without this the redirect effect replaces the screen with `/feeds` and the
 * `code` query param is lost with it, so the user cannot complete the flow at all.
 */
const SELF_ROUTED_ROUTES = ["reset-password", "set-password"] as const;

/**
 * True when `pathname` is one of the credential-bearing routes above.
 *
 * Compared per segment rather than with a bare `includes` so an unrelated route
 * that merely embeds the name (a `/wiki/reset-password-guide` article, say) is
 * not accidentally exempted. Tolerates the `(auth)` group prefix — `usePathname`
 * strips group segments, but matching on the trailing segment keeps this correct
 * if a caller passes a raw href instead.
 */
export function isSelfRoutedPath(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) return false;
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, "");
  return SELF_ROUTED_ROUTES.some(
    (route) => path === `/${route}` || path.endsWith(`/${route}`),
  );
}

/**
 * Gate for the post-login redirect effect.
 *
 * `rootNavReady` is the one that bites. expo-router silently DROPS a
 * `router.replace` issued before the root navigator has mounted — it does not
 * throw, so a try/catch around the navigation never fires. Because the effect
 * sets its `hasRedirected` re-entry guard *before* navigating, a dropped
 * navigation strands an authenticated session on the login screen (or on the
 * loading spinner) until a manual reload, and the effect never retries.
 *
 * It presents intermittently: whether the navigator has mounted by the time the
 * user and org queries settle is a race, so the same code path works most of the
 * time and fails on a slow load or a cold SSO round-trip.
 *
 * `pathname` makes the gate route-aware. It is optional because omitting it
 * preserves the historical always-redirect behavior, which is the safe default
 * for any future caller that has no route to offer: a missing pathname must
 * never *block* a redirect and strand a session.
 */
export function shouldAttemptPostLoginRedirect(state: {
  initialized: boolean;
  rootNavReady: boolean;
  isUserLoading: boolean;
  isPreferredOrgLoading: boolean;
  /**
   * `GET /iam/organizations/my` is the ONLY source of the user's real org
   * memberships (see auth.context), and the redirect branches on whether that
   * list is empty. Firing before it settles reads "no orgs" and sends a perfectly
   * valid user to the pending screen — permanently, because `hasRedirected`
   * latches and the effect never retries.
   */
  isOrganizationsLoading: boolean;
  hasUser: boolean;
  hasRedirected: boolean;
  pathname?: string | null;
}): boolean {
  return (
    state.initialized &&
    state.rootNavReady &&
    !state.isUserLoading &&
    !state.isPreferredOrgLoading &&
    !state.isOrganizationsLoading &&
    state.hasUser &&
    !state.hasRedirected &&
    !isSelfRoutedPath(state.pathname)
  );
}
